<template>
    <SettingsLayout>
        <SettingsSubHeader v-if="team">
            <template #title>
                <template>
                    {{ $t("billing.team_title") }}: {{ team?.name }}
                </template>
            </template>
        </SettingsSubHeader>

        <div class="mb-5 max-w-7xl px-4 sm:px-6 sm:pb-10 lg:px-6 mx-auto">
            <Alert v-if="isPartOfPaidTeam" variant="info" class="mt-6">
                <span class="font-medium tracking-[-0.02em]">
                    {{ $t("billing.paid_team_member.title") }}
                </span>
                <span class="mt-1 text-sm block tracking-[-0.01em]">
                    {{ $t("billing.paid_team_member.description") }}
                </span>
            </Alert>

            <div v-if="activePlan || isPastDue" class="mt-4 sm:mt-[42px]">
                <div
                    v-if="isPastDue"
                    class="gap-4 rounded-t-xl border-amber-200 bg-amber-50 px-6 py-4 sm:flex-row sm:items-center flex flex-col items-start justify-between border border-b-0"
                >
                    <div class="gap-3 flex items-center">
                        <div
                            class="h-8 w-8 bg-amber-100 flex shrink-0 items-center justify-center rounded-full"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                class="h-4 w-4 text-amber-600"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                        </div>
                        <div>
                            <p class="text-sm font-medium text-amber-800">
                                {{ $t("billing.past_due.title") }}
                            </p>
                            <p
                                v-if="paymentFailureReason"
                                class="text-sm text-amber-700"
                            >
                                {{ paymentFailureReason }}
                            </p>
                        </div>
                    </div>
                    <div
                        class="gap-3 sm:w-auto sm:flex-row sm:items-center flex w-full flex-col"
                    >
                        <UpdatePaymentMethodsModal
                            :client-secret="clientSecret"
                            :payment-methods="paymentMethods"
                            :billing-address
                            :active-plan
                            :default-payment-method="defaultPaymentMethod"
                        >
                            <Button size="sm" variant="outline">
                                {{
                                    $t("billing.past_due.update_payment_method")
                                }}
                            </Button>
                        </UpdatePaymentMethodsModal>
                        <Button
                            size="sm"
                            :disabled="retryingPayment"
                            @click="retryPayment"
                        >
                            <svg
                                v-if="retryingPayment"
                                class="mr-2 h-4 w-4 animate-spin"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    class="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    stroke-width="4"
                                ></circle>
                                <path
                                    class="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                            </svg>
                            {{ $t("billing.past_due.retry_payment") }}
                        </Button>
                    </div>
                </div>
                <div
                    class="bg-white shadow-xs lg:grid-cols-[1fr_428px] grid grid-cols-1 border"
                    :class="
                        isPastDue
                            ? 'rounded-b-xl border-amber-200'
                            : 'rounded-xl border-iron-200'
                    "
                >
                    <div class="gap-4 p-6 flex">
                        <div class="shrink-0">
                            <ProfessionalTierLogo />
                        </div>
                        <div>
                            <p
                                class="text-lg font-medium text-iron-700 tracking-[-0.02em]"
                            >
                                {{ $t("billing.professional_plan_card.title") }}
                            </p>
                            <p
                                class="text-sm text-iron-500 mt-[1px] max-w-[398px] tracking-[0.01em]"
                            >
                                {{
                                    $t(
                                        "billing.professional_plan_card.description",
                                    )
                                }}
                            </p>
                        </div>
                    </div>
                    <div
                        class="bg-iron-50 p-6 lg:border-l lg:border-t-0 lg:border-iron-200 relative border-t"
                    >
                        <p class="text-2xl font-medium tracking-[-0.02em]">
                            {{
                                formatPriceCurrency(
                                    activePlan.price,
                                    activePlan.currency,
                                )
                            }}
                        </p>

                        <p class="text-sm text-iron-500 tracking-[-0.01em]">
                            {{
                                activePlan.recurring === "monthly"
                                    ? $t(
                                          "billing.professional_plan_card.per_month",
                                      )
                                    : $t(
                                          "billing.professional_plan_card.per_year",
                                      )
                            }}
                        </p>

                        <p
                            class="mt-8 text-sm text-iron-500 tracking-[-0.01em]"
                        >
                            {{
                                $t(
                                    "billing.professional_plan_card.billing_period",
                                )
                            }}
                        </p>

                        <p
                            v-if="subscription"
                            class="text-sm font-medium text-iron-600"
                        >
                            <span v-if="!subscription.ends_at">
                                <span v-if="activePlan.recurring === 'monthly'">
                                    {{
                                        $t(
                                            "billing.professional_plan_card.monthly",
                                        )
                                    }}
                                </span>

                                <span v-if="activePlan.recurring === 'yearly'">
                                    {{
                                        $t(
                                            "billing.professional_plan_card.yearly",
                                        )
                                    }}
                                </span>

                                <span v-if="nextBillingCycle">
                                    ({{
                                        $t(
                                            "billing.professional_plan_card.renews",
                                        )
                                    }}
                                    {{
                                        format(
                                            new Date(nextBillingCycle),
                                            "PPP",
                                        )
                                    }})
                                </span>
                            </span>

                            <span
                                v-else-if="
                                    isBefore(
                                        new Date(),
                                        new Date(subscription.ends_at),
                                    )
                                "
                            >
                                aktivní do
                                {{
                                    format(
                                        new Date(subscription.ends_at),
                                        "PPP",
                                    )
                                }}
                            </span>
                        </p>

                        <div
                            v-if="subscription && !subscription.ends_at"
                            class="gap-4 sm:flex-row sm:items-center relative z-10 mt-[31px] flex flex-col items-start"
                        >
                            <SwitchBillingPeriodModal
                                :switch-plan="switchPlanId"
                                :team-id="team ? team.id : undefined"
                            >
                                <Button
                                    class="group mt-4 gap-1 sm:mt-0"
                                    variant="ghost"
                                >
                                    {{
                                        activePlan.recurring === "yearly"
                                            ? $t(
                                                  "billing.professional_plan_card.switch_to_monthly_billing",
                                              )
                                            : $t(
                                                  "billing.professional_plan_card.switch_to_anual_billing",
                                              )
                                    }}

                                    <ArrowRight
                                        class="ease-in group-hover:translate-x-1 transition-all duration-150"
                                        :size="20"
                                    />
                                </Button>
                            </SwitchBillingPeriodModal>
                            <Button
                                @click="cancelSubscription"
                                class="mt-4 sm:mt-0"
                                variant="ghost"
                            >
                                {{
                                    $t("billing.professional_plan_card.cancel")
                                }}</Button
                            >
                        </div>
                        <div
                            v-else
                            class="gap-4 relative z-10 mt-[31px] flex justify-end"
                        >
                            <Button
                                :loading="resumeForm.processing"
                                :recently-successful="
                                    resumeForm.recentlySuccessful
                                "
                                @click="resumeSubscription"
                                class="mt-4 sm:mt-0"
                            >
                                {{ $t("billing.card.resume") }}</Button
                            >
                        </div>
                    </div>
                </div>
            </div>

            <div
                v-if="activePlan && additionalMembers.length > 0"
                class="mt-4 sm:mt-[42px]"
            >
                <div
                    class="rounded-xl border-iron-200 bg-white shadow-xs lg:grid-cols-[1fr_428px] grid grid-cols-1 border"
                >
                    <div class="gap-4 p-6 flex">
                        <div class="shrink-0">
                            <ProfessionalTeamLogo />
                        </div>
                        <div>
                            <p
                                class="text-lg font-medium text-iron-700 tracking-[-0.02em]"
                            >
                                {{ $t("billing.team_billing_card.title") }}
                            </p>
                            <p
                                class="text-sm text-iron-500 mt-[1px] max-w-[440px] tracking-[0.01em]"
                            >
                                {{ $t("billing.team_billing_card.you_own") }}
                                <b>
                                    {{ teams.length - 1 }}
                                    {{
                                        $t("billing.team_billing_card.team", {
                                            count: teams.length - 1,
                                        })
                                    }}<span v-if="locale === 'cs'">&nbsp;</span
                                    >{{
                                        $t(
                                            "billing.team_billing_card.with_a_total_of",
                                        )
                                    }}
                                    {{ additionalMembers.length }}
                                    {{
                                        $t("billing.team_billing_card.seat", {
                                            count: additionalMembers.length,
                                        })
                                    }}{{ "." }}</b
                                ><br />
                                {{
                                    $t(
                                        "billing.team_billing_card.team_billing_reminder",
                                    )
                                }}
                            </p>

                            <div class="mt-4">
                                <div class="-space-x-1 flex overflow-hidden">
                                    <ImageZaptimeShape
                                        v-for="member of additionalMembers"
                                        :key="member.id"
                                        class="size-10 ring-white relative inline-block ring-2"
                                        has-border
                                        :src="member.profile_photo_url"
                                    />

                                    <!--  <AdditinonalMembersIcon
                                        >+2</AdditinonalMembersIcon
                                     -->
                                </div>
                            </div>

                            <div class="mt-6">
                                <Link href="/teams">
                                    <Button
                                        as="span"
                                        class="group gap-1"
                                        variant="ghost"
                                    >
                                        {{
                                            $t(
                                                "billing.team_billing_card.manage_your_teams",
                                            )
                                        }}
                                        <ArrowRight
                                            :size="20"
                                            class="ease-in group-hover:translate-x-1 transition-all duration-150"
                                    /></Button>
                                </Link>
                            </div>
                        </div>
                    </div>
                    <div
                        class="bg-iron-50 p-6 lg:border-l lg:border-t-0 lg:border-iron-200 relative border-t"
                    >
                        <p class="text-2xl font-medium tracking-[-0.02em]">
                            {{
                                formatPriceCurrency(
                                    activePlan.price * additionalMembers.length,
                                    activePlan.currency,
                                )
                            }}
                        </p>

                        <p class="text-sm text-iron-500 tracking-[-0.01em]">
                            {{
                                activePlan.recurring === "monthly"
                                    ? $t(
                                          "billing.professional_plan_card.per_month",
                                      )
                                    : $t(
                                          "billing.professional_plan_card.per_year",
                                      )
                            }}
                        </p>

                        <p
                            class="mt-8 text-sm text-iron-500 tracking-[-0.01em]"
                        >
                            {{
                                $t(
                                    "billing.professional_plan_card.billing_period",
                                )
                            }}
                        </p>

                        <p
                            v-if="subscription"
                            class="text-sm font-medium text-iron-600"
                        >
                            <span v-if="!subscription.ends_at">
                                <span v-if="activePlan.recurring === 'monthly'">
                                    {{
                                        $t(
                                            "billing.professional_plan_card.monthly",
                                        )
                                    }}
                                </span>

                                <span v-if="activePlan.recurring === 'yearly'">
                                    {{
                                        $t(
                                            "billing.professional_plan_card.yearly",
                                        )
                                    }}
                                </span>

                                <span v-if="nextBillingCycle">
                                    ({{
                                        $t(
                                            "billing.professional_plan_card.renews",
                                        )
                                    }}
                                    {{
                                        format(
                                            new Date(nextBillingCycle),
                                            "PPP",
                                        )
                                    }})
                                </span>
                            </span>

                            <span
                                v-else-if="
                                    isBefore(
                                        new Date(),
                                        new Date(subscription.ends_at),
                                    )
                                "
                            >
                                aktivní do
                                {{
                                    format(
                                        new Date(subscription.ends_at),
                                        "PPP",
                                    )
                                }}
                            </span>
                        </p>

                        <!--                        <div
                            v-if="subscription && !subscription.ends_at"
                            class="relative z-10 mt-[31px] flex flex-col items-start gap-4 sm:flex-row sm:items-center"
                        >
                            <SwitchBillingPeriodModal
                                :switch-plan="switchPlanId"
                                :team-id="team ? team.id : undefined"
                            >
                                <Button
                                    class="group mt-4 gap-1 sm:mt-0"
                                    variant="ghost"
                                >
                                    {{
                                        activePlan.recurring === "yearly"
                                            ? $t(
                                                  "billing.professional_plan_card.switch_to_monthly_billing",
                                              )
                                            : $t(
                                                  "billing.professional_plan_card.switch_to_anual_billing",
                                              )
                                    }}

                                    <ArrowRight
                                        class="transition-all duration-150 ease-in group-hover:translate-x-1"
                                        :size="20"
                                    />
                                </Button>
                            </SwitchBillingPeriodModal>
                        </div> -->
                    </div>
                </div>
            </div>
            <div v-if="!activePlan" class="mt-4 sm:mt-[42px]">
                <div
                    class="rounded-xl border-iron-200 bg-white shadow-xs lg:grid-cols-[1fr_428px] grid grid-cols-1 border"
                >
                    <div class="gap-4 p-6 flex">
                        <div class="shrink-0">
                            <PersonalTierLogo />
                        </div>
                        <div>
                            <p
                                class="text-lg font-medium text-iron-700 tracking-[-0.02em]"
                            >
                                {{ $t("billing.personal_plan_card.title") }}
                            </p>
                            <p
                                class="text-sm text-iron-500 mt-[1px] max-w-[398px] tracking-[0.01em]"
                            >
                                {{
                                    $t("billing.personal_plan_card.description")
                                }}
                            </p>
                        </div>
                    </div>
                    <div
                        class="p-6 lg:border-l lg:border-t-0 lg:border-iron-200 relative border-t"
                    >
                        <img
                            width="428"
                            height="180"
                            src="https://zaptime.s3.eu-central-1.amazonaws.com/application-assets/card-bg.png"
                            class="right-0 top-0 rounded-b-xl md:rounded-none absolute z-0 h-full w-full object-cover"
                        />
                        <p
                            class="text-sm leading-5 text-iron-700 relative z-10 max-w-[361px] tracking-[-0.01em]"
                        >
                            <span class="text-base font-medium leading-6">
                                {{
                                    $t(
                                        "billing.personal_plan_card.upgrade_title",
                                    )
                                }}</span
                            ><br />
                            {{
                                $t(
                                    "billing.personal_plan_card.upgrade_description",
                                )
                            }}
                        </p>

                        <div class="gap-4 relative z-10 mt-[31px] flex">
                            <PaymentModal
                                :client-secret="clientSecret"
                                :plans="plans"
                                :billing-address="billingAddress"
                                :teamId="team ? team.id : undefined"
                            >
                                <Button class="mt-4 sm:mt-0" size="sm">
                                    {{
                                        $t(
                                            "billing.personal_plan_card.upgrade_cta",
                                        )
                                    }}</Button
                                >
                            </PaymentModal>

                            <Button
                                class="mt-4 sm:mt-0"
                                as="a"
                                :href="
                                    locale === 'cs'
                                        ? 'https://zaptime.cz/#pricing'
                                        : 'https://zaptime.app#pricing'
                                "
                                target="_blank"
                                variant="outline"
                                size="sm"
                            >
                                {{
                                    $t(
                                        "billing.personal_plan_card.compare_plans",
                                    )
                                }}</Button
                            >
                        </div>
                    </div>
                </div>
            </div>

            <!-- <div class="mt-6 grid gap-6 sm:grid-cols-2">
        <div class="rounded-xl border border-iron-200 bg-white p-6 shadow-xs">
          <div>
            <p class="font-medium tracking-[-0.01em] text-iron-700">
              {{ $t("billing.billing_card.title") }}
            </p>

            <p class="text-sm tracking-[-0.01em] text-iron-500">
              {{ $t("billing.billing_card.description") }}
            </p>
          </div>

          <div class="mt-6 flex flex-col items-center justify-center gap-3">
            <BillingInformation class="mt-4" :billing-address="billingAddress">
              <button>
                <Button variant="outline" as="div" size="icon">
                  <svg
                    width="16"
                    height="20"
                    viewBox="0 0 16 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9.66665 6.6665H4.66665M11.3333 9.99984H4.66665M8.83331 13.3332H4.66665M1.33331 1.6665V18.3332L2.99998 17.4998L4.66665 18.3332L6.33331 17.4998L7.99998 18.3332L9.66665 17.4998L11.3333 18.3332L13 17.4998L14.6666 18.3332V1.6665L13 2.49984L11.3333 1.6665L9.66665 2.49984L7.99998 1.6665L6.33331 2.49984L4.66665 1.6665L2.99998 2.49984L1.33331 1.6665Z"
                      stroke="#70707B"
                      stroke-width="1.75"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </Button>
                <div class="mt-3 text-sm font-medium text-z-primary-600">
                  Add your billing details
                </div>
              </button>
            </BillingInformation>
          </div>
        </div>
      </div> -->

            <div class="mt-6 gap-6 sm:grid-cols-2 grid">
                <div
                    v-if="paymentMethods.length > 0"
                    class="rounded-xl border-iron-200 bg-white p-6 shadow-xs border"
                >
                    <div class="flex justify-between">
                        <div>
                            <p
                                class="font-medium text-iron-700 tracking-[-0.01em]"
                            >
                                {{ $t("billing.card.payment_method") }}
                            </p>

                            <p class="text-sm text-iron-500 tracking-[-0.01em]">
                                {{ $t("billing.card.default_payment_method") }}
                            </p>
                        </div>

                        <div>
                            <UpdatePaymentMethodsModal
                                :client-secret="clientSecret"
                                :payment-methods="paymentMethods"
                                :billing-address
                                :active-plan
                                :default-payment-method="defaultPaymentMethod"
                            >
                                <Button size="sm"> {{ $t("update") }}</Button>
                            </UpdatePaymentMethodsModal>
                        </div>
                    </div>

                    <div v-if="defaultPaymentMethod" class="mt-5 gap-6 grid">
                        <Alert v-if="isCardAboutToExpire" variant="error">
                            <span class="font-medium tracking-[-0.02em]">
                                {{ $t("billing.card.alert_card_expire.title") }}
                            </span>

                            <span class="mt-2 text-sm block tracking-[-0.01em]">
                                {{
                                    $t(
                                        "billing.card.alert_card_expire.description",
                                    )
                                }}
                            </span>
                        </Alert>

                        <div class="gap-4 flex">
                            <div>
                                <div
                                    class="h-10 rounded-md border-iron-200 px-2 flex items-center justify-center border"
                                >
                                    <IconVisa
                                        v-if="
                                            defaultPaymentMethod.brand ===
                                            'visa'
                                        "
                                    />
                                    <IconMastercard
                                        v-if="
                                            defaultPaymentMethod.brand ===
                                            'mastercard'
                                        "
                                    />
                                </div>
                            </div>
                            <div>
                                <p class="text-sm font-medium text-iron-700">
                                    <span class="capitalize">{{
                                        defaultPaymentMethod.brand
                                    }}</span>
                                    {{ $t("billing.card.ending_in") }}
                                    {{ defaultPaymentMethod.last4 }}
                                </p>
                                <p
                                    class="text-sm text-iron-500 tracking-[-0.01em]"
                                >
                                    {{ $t("billing.card.expiry_short") }}
                                    {{ defaultPaymentMethod.exp_month }}/{{
                                        defaultPaymentMethod.exp_year
                                    }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    class="rounded-xl border-iron-200 bg-white p-6 shadow-xs border"
                >
                    <div class="flex justify-between">
                        <div>
                            <p
                                class="font-medium text-iron-700 tracking-[-0.01em]"
                            >
                                {{ $t("billing.billing_card.title") }}
                            </p>

                            <p class="text-sm text-iron-500 tracking-[-0.01em]">
                                {{ $t("billing.billing_card.description") }}
                            </p>
                        </div>

                        <div>
                            <BillingInformation
                                class="mt-4"
                                :billing-address="billingAddress"
                            >
                                <Button size="sm">
                                    {{ $t("billing.billing_card.update") }}
                                </Button>
                            </BillingInformation>
                        </div>
                    </div>

                    <div class="mt-5 gap-6 md:grid-cols-2 grid grid-cols-1">
                        <div class="gap-3 flex">
                            <div><Mail class="text-z-primary-600"></Mail></div>
                            <div>
                                <p class="text-sm font-medium text-iron-600">
                                    {{
                                        $t("billing.billing_card.email_address")
                                    }}
                                </p>
                                <p
                                    class="text-sm text-iron-600 tracking-[-0.01em]"
                                >
                                    {{ billingAddress.email }}
                                </p>
                            </div>
                        </div>
                        <div
                            v-if="
                                billingAddress.first_name ||
                                billingAddress.last_name
                            "
                            class="gap-3 flex"
                        >
                            <div>
                                <UserIcon class="text-z-primary-600"></UserIcon>
                            </div>
                            <div>
                                <p class="text-sm font-medium text-iron-600">
                                    {{ $t("billing.billing_card.contact") }}
                                </p>
                                <p
                                    class="text-sm text-iron-600 tracking-[-0.01em]"
                                >
                                    {{ billingAddress.first_name }}
                                    {{ billingAddress.last_name }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div class="mt-5 gap-6 md:grid-cols-2 grid">
                        <div class="gap-3 flex">
                            <div>
                                <MapPin class="text-z-primary-600"></MapPin>
                            </div>

                            <div>
                                <p class="text-sm font-medium text-iron-600">
                                    {{ $t("billing.billing_card.address") }}
                                </p>
                                <p
                                    class="text-sm text-iron-600 tracking-[-0.01em]"
                                >
                                    {{ billingAddress.first_name }}
                                    {{ billingAddress.last_name }}
                                </p>
                                <p
                                    class="text-sm text-iron-600 tracking-[-0.01em]"
                                >
                                    {{ billingAddress.street }}
                                </p>
                                <p
                                    class="text-sm text-iron-600 tracking-[-0.01em]"
                                >
                                    {{ billingAddress.postal_code }}
                                    {{ billingAddress.city }}
                                </p>
                                <p
                                    class="text-sm text-iron-600 tracking-[-0.01em]"
                                >
                                    {{
                                        countries.find(
                                            (c) =>
                                                c.value ===
                                                billingAddress.country,
                                        )?.label
                                    }}
                                </p>
                            </div>
                        </div>
                        <div
                            v-if="billingAddress.tax_id || billingAddress.crn"
                            class="gap-3 flex"
                        >
                            <div><Info class="text-z-primary-600"></Info></div>

                            <div>
                                <div v-if="billingAddress.crn">
                                    <p
                                        class="text-sm font-medium text-iron-600"
                                    >
                                        {{ $t("billing.billing_card.crn") }}
                                    </p>
                                    <p
                                        class="text-sm text-iron-600 tracking-[-0.01em]"
                                    >
                                        {{ billingAddress.crn }}
                                    </p>
                                </div>

                                <div v-if="billingAddress.tax_id">
                                    <p
                                        class="text-sm font-medium text-iron-600"
                                    >
                                        {{ $t("billing.billing_card.tax_id") }}
                                    </p>
                                    <p
                                        class="text-sm text-iron-600 tracking-[-0.01em]"
                                    >
                                        {{ billingAddress.tax_id }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Invoices :is-team-billing="isTeamBilling" :team="team" />
        </div>
    </SettingsLayout>
</template>

<script lang="ts" setup>
import BillingInformation from "./components/BillingInformation.vue";
import route from "ziggy-js";
import Invoices from "./components/Invoices.vue";
import { computed, ref } from "vue";
import type {
    BillingAddress,
    Plan,
    Subscription,
    PaymentMethod,
    PaymentFailure,
} from "./types";
import PaymentModal from "./components/PaymentModal.vue";
import { Button } from "@/components/ui/button";
import { Link, router, useForm } from "@inertiajs/vue3";
import useConfirm from "../ConfirmDialog/composables/useConfirm";
import { useI18n } from "vue-i18n";
import {
    ArrowRight,
    Info,
    Mail,
    MapPin,
    User as UserIcon,
} from "lucide-vue-next";
import { type Team } from "../Teams/types/Team";
import SettingsLayout from "@/Layouts/SettingsLayout.vue";
import SettingsSubHeader from "@/Layouts/components/SettingsSubHeader.vue";
import PersonalTierLogo from "./components/PersonalTierLogo.svg";
import ProfessionalTeamLogo from "./components/ProfessionalTeamLogo.svg";
import ProfessionalTierLogo from "./components/ProfessionalTierLogo.svg";
import ImageZaptimeShape from "../../../../components/ImageZaptimeShape.vue";
import { countries } from "./components/countries";
import { useLocalisedTzFormat } from "@/composables/useLocalisedTzFormat";
import SwitchBillingPeriodModal from "./components/SwitchBillingPeriodModal.vue";
import IconVisa from "./components/IconVisa.svg";
import IconMastercard from "./components/IconMastercard.svg";
import UpdatePaymentMethodsModal from "./components/UpdatePaymentMethodsModal.vue";
import { isBefore, isPast } from "date-fns";
import { Alert } from "@/components/ui/alert";
import { differenceInMonths } from "date-fns";
import { formatPriceCurrency } from "./utils/formatPriceCurrency";
import AdditinonalMembersIcon from "./components/AdditionalMembersIcon.vue";
import type { User } from "../EventTypes/types/EventType";

const props = defineProps<{
    billingAddress: BillingAddress;
    subscription?: Subscription;
    subscribed: boolean;
    clientSecret: string;
    plans: Plan[];
    team?: Team;
    teams: Team[];
    additionalMembers: User[];
    nextBillingCycle?: string;
    paymentMethods: PaymentMethod[];
    defaultPaymentMethod?: PaymentMethod;
    isPartOfPaidTeam?: boolean;
    paymentFailure?: PaymentFailure;
}>();

const { show } = useConfirm();
const { t, locale } = useI18n();
const { format } = useLocalisedTzFormat();
const resumeForm = useForm({});
const retryingPayment = ref(false);

const isCanceledSubWithEndsAt = computed(() => {
    return (
        props.subscription?.ends_at &&
        isPast(new Date(props.subscription?.ends_at))
    );
});

const isPastDue = computed(() => {
    return props.subscription?.stripe_status === "past_due";
});

async function retryPayment() {
    retryingPayment.value = true;
    router.post(
        route("app.subscription.retry-payment"),
        {},
        {
            onFinish: () => {
                retryingPayment.value = false;
            },
            onError: (errors) => {
                console.error("Failed to retry payment:", errors);
            },
        },
    );
}

const paymentFailureReason = computed(() => {
    if (!props.paymentFailure) {
        return null;
    }

    const { code, decline_code } = props.paymentFailure;

    // Try to get translated message based on decline_code or code
    const key = decline_code || code;
    if (key) {
        const translationKey = `billing.past_due.reasons.${key}`;
        const translated = t(translationKey);
        // If translation exists (not same as key), use it
        if (translated !== translationKey) {
            return translated;
        }
    }

    // Fallback to Stripe's message or generic message
    return props.paymentFailure.message || t("billing.past_due.unknown_reason");
});

const activePlan = computed(() => {
    if (isCanceledSubWithEndsAt.value) {
        return undefined;
    }

    return props.plans.find(
        (plan) => plan.plan_id === props.subscription?.stripe_price,
    );
});

const isTeamBilling = computed(() => {
    return props.team !== undefined;
});

const switchPlanId = computed(() => {
    const plan = props.plans.find(
        (plan) =>
            activePlan.value &&
            plan.id ===
                activePlan.value.id.replace(
                    activePlan.value.recurring === "monthly"
                        ? "MONTHLY"
                        : "YEARLY",
                    activePlan.value.recurring === "monthly"
                        ? "YEARLY"
                        : "MONTHLY",
                ),
    );

    if (plan) {
        return plan;
    }

    throw new Error("Invalid active plan!");
});

const isCardAboutToExpire = computed(() => {
    if (!props.defaultPaymentMethod) {
        return false;
    }

    const expireDate = new Date(
        props.defaultPaymentMethod.exp_year,
        props.defaultPaymentMethod.exp_month - 1,
        1,
    );

    const today = new Date();
    const isInTwoMonths = differenceInMonths(expireDate, today) < 3;

    return isInTwoMonths;
});

async function cancelSubscription() {
    const res = await show({
        header: t("billing.card.cancel_subscription"),
        message: t("billing.card.cancel_subscription_description"),
        confirm: t("billing.card.cancel_subscription"),
        cancel: t("billing.card.cancel_subscription_cancelation"),
    });

    if (res) {
        router.post(
            route("app.subscription.cancel", { team: props.team?.id }),
            {},
            {
                onError: (error) => {
                    console.error(error);
                },
            },
        );
    }
}

async function resumeSubscription() {
    resumeForm.post(
        route("app.subscription.resume", { team: props.team?.id }),
        {
            onError: (error) => {
                console.log(error);
            },
        },
    );
}
</script>
